import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridToolbar, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useParams } from 'react-router-dom';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const ReportDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();

  const [report, setReport] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const [passengersPage, setPassengersPage] = useState(1);
  const [passengersPageSize, setPassengersPageSize] = useState(25);
  const [passengersTotalCount, setPassengersTotalCount] = useState(0);
  const [scanPage, setScanPage] = useState(1);
  const [scanPageSize, setScanPageSize] = useState(25);
  const [scanTotalCount, setScanTotalCount] = useState(0);

  useEffect(() => {
    fetchReportDetails(1, passengersPageSize);
    fetchScanHistory(1, scanPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchReportDetails(passengersPage, passengersPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengersPage, passengersPageSize]);

  useEffect(() => {
    fetchScanHistory(scanPage, scanPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanPage, scanPageSize]);

  const fetchReportDetails = async (pPage, pLimit) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/reports/excursions/${id}?page=${pPage}&limit=${pLimit}`,
        { headers: { token } }
      );
      const data = await response.json();
      if (data.success) {
        setReport(data.data.excursion);
        setPassengers(data.data.passengers);
        setPassengersTotalCount(data.data.pagination?.total || 0);
      } else {
        setAlert({ show: true, message: data.message || 'Error fetching report details', type: 'error' });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report details:', error);
      setAlert({ show: true, message: 'Error fetching report details', type: 'error' });
      setLoading(false);
    }
  };

  const fetchScanHistory = async (sPage, sLimit) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/reports/scan-history/${id}?page=${sPage}&limit=${sLimit}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setScanHistory(data.data);
        setScanTotalCount(data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching scan history:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'attended':
        return 'success';
      case 'no-show':
        return 'error';
      case 'cancelled':
        return 'warning';
      case 'paid-but-no-show':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentFlowColor = (status) => {
    switch (status) {
      case 'paid-with-qr':
        return 'success';
      case 'paid-no-qr':
        return 'warning';
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getQRStatusColor = (hasQR) => {
    return hasQR ? 'success' : 'error';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === 'Not Scheduled') return 'Not Scheduled';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return 'Not Scheduled';
      return date.toLocaleString('en-GB');
    } catch (error) {
      return 'Not Scheduled';
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // DataGrid columns for passenger details
  const passengerColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
    },
    {
      field: 'nationality',
      headerName: 'Nationality',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.value.toUpperCase()} color={getStatusColor(params.value)} size="small" />
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment',
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.value.toUpperCase()} color={getPaymentColor(params.value)} size="small" />
      ),
    },
    {
      field: 'paymentFlowStatus',
      headerName: 'Payment Flow',
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.value.toUpperCase()} color={getPaymentFlowColor(params.value)} size="small" />
      ),
    },
    {
      field: 'hasQRCode',
      headerName: 'QR Code',
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={params.value ? 'YES' : 'NO'} color={getQRStatusColor(params.value)} size="small" />
      ),
    },
    {
      field: 'scanStatus',
      headerName: 'Scanned',
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={params.value === 1 ? 'YES' : 'NO'} color={params.value === 1 ? 'success' : 'error'} size="small" />
      ),
    },
    {
      field: 'seatNumber',
      headerName: 'Seat',
      flex: 0.8,
    },
    {
      field: 'specialRequests',
      headerName: 'Special Requests',
      flex: 1.2,
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box m="20px">
        <Alert severity="error">Report not found</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Header */}
      <Box display="flex" alignItems="center" mb="20px">
        <IconButton onClick={() => navigate('/reports/excursion')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h2" color={colors.grey[100]} fontWeight="bold">
          Report Details
        </Typography>
      </Box>

      {/* Report Summary Cards */}
      <Grid container spacing={3} mb="30px">
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Total Booked
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.greenAccent[500]}>
                    {report.totalBooked}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: colors.greenAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Attended
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.blueAccent[500]}>
                    {report.totalAttended}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Paid
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.greenAccent[500]}>
                    {report.totalPaid}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.greenAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Unpaid
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.redAccent[500]}>
                    {report.totalUnpaid}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.redAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QR Code and Payment Flow Summary Cards */}
      <Grid container spacing={3} mb="30px">
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Paid with QR
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.greenAccent[500]}>
                    {report.totalPaidWithQR || 0}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.greenAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Paid No QR
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.blueAccent[500]}>
                    {report.totalPaidNoQR || 0}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Paid But No Show
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.redAccent[500]}>
                    {report.totalPaidButNoShow || 0}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: colors.redAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Scan Rate
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.blueAccent[500]}>
                    {report.totalPaidWithQR > 0
                      ? ((report.totalAttended / report.totalPaidWithQR) * 100).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
                <HistoryIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Excursion Details */}
      <Card sx={{ backgroundColor: colors.primary[400], mb: '30px' }}>
        <CardContent>
          <Typography variant="h4" color={colors.grey[100]} fontWeight="bold" mb="20px">
            Excursion Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Excursion Name
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.excursionName}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Departure Location
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.departureName}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Guide
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.guide}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Departure Date
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {formatDate(report.departDate)}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Excursion Date & Time
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {formatDateTime(report.excursionDateTime)}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Duration & Price
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.duration} - AED {report.price}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ '& .MuiTab-root': { color: colors.grey[100] } }}>
          <Tab label="Passenger Details" />
          <Tab label="Payment Summary" />
          <Tab label="QR Scan History" />
          <Tab label="Full History" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Box
          height="400px"
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .name-column--cell': {
              color: colors.greenAccent[300],
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent[700],
              borderBottom: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: colors.primary[400],
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
              backgroundColor: colors.blueAccent[700],
            },
            '& .MuiCheckbox-root': {
              color: `${colors.greenAccent[200]} !important`,
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
              fontSize: 14,
            },
          }}
        >
          <DataGrid
            rows={passengers}
            columns={passengerColumns}
            getRowId={(row) => row.id}
            components={{ Toolbar: CustomToolbar }}
            loading={loading}
            paginationMode="server"
            page={passengersPage - 1}
            pageSize={passengersPageSize}
            rowCount={passengersTotalCount}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(p) => setPassengersPage(p + 1)}
            onPageSizeChange={(s) => { setPassengersPageSize(s); setPassengersPage(1); }}
            sortingOrder={['asc', 'desc']}
          />
        </Box>
      )}

      {tabValue === 1 && (
        <Card sx={{ backgroundColor: colors.primary[400] }}>
          <CardContent>
            <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb="20px">
              Payment Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                  <Typography variant="h4" color={colors.greenAccent[500]} fontWeight="bold">
                    AED {report.totalPaid * report.price}
                  </Typography>
                  <Typography color={colors.grey[100]}>Total Paid Amount</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                  <Typography variant="h4" color={colors.redAccent[500]} fontWeight="bold">
                    AED {report.totalUnpaid * report.price}
                  </Typography>
                  <Typography color={colors.grey[100]}>Outstanding Amount</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                  <Typography variant="h4" color={colors.blueAccent[500]} fontWeight="bold">
                    {((report.totalPaid / report.totalBooked) * 100).toFixed(1)}%
                  </Typography>
                  <Typography color={colors.grey[100]}>Payment Rate</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 2 && (
        <Box
          height="400px"
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .name-column--cell': {
              color: colors.greenAccent[300],
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent[700],
              borderBottom: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: colors.primary[400],
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
              backgroundColor: colors.blueAccent[700],
            },
            '& .MuiCheckbox-root': {
              color: `${colors.greenAccent[200]} !important`,
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
              fontSize: 14,
            },
          }}
        >
          <DataGrid
            rows={scanHistory}
            columns={[
              {
                field: 'passengerName',
                headerName: 'Passenger',
                flex: 1,
                cellClassName: 'name-column--cell',
              },
              {
                field: 'contractNo',
                headerName: 'Contract No',
                flex: 1,
              },
              {
                field: 'paymentStatus',
                headerName: 'Payment',
                flex: 0.8,
                renderCell: (params) => (
                  <Chip label={params.value.toUpperCase()} color={getPaymentColor(params.value)} size="small" />
                ),
              },
              {
                field: 'currentStatus',
                headerName: 'Status',
                flex: 1,
                renderCell: (params) => (
                  <Chip label={params.value.toUpperCase()} color={getPaymentFlowColor(params.value)} size="small" />
                ),
              },
              {
                field: 'hasQRCode',
                headerName: 'Has QR',
                flex: 0.8,
                renderCell: (params) => (
                  <Chip label={params.value ? 'YES' : 'NO'} color={getQRStatusColor(params.value)} size="small" />
                ),
              },
              {
                field: 'scanStatus',
                headerName: 'Scanned',
                flex: 0.8,
                renderCell: (params) => (
                  <Chip
                    label={params.value === 'scanned' ? 'YES' : 'NO'}
                    color={params.value === 'scanned' ? 'success' : 'error'}
                    size="small"
                  />
                ),
              },
              {
                field: 'qrGeneratedAt',
                headerName: 'QR Generated',
                flex: 1.2,
                renderCell: (params) => (params.value ? formatDateTime(params.value) : 'N/A'),
              },
              {
                field: 'lastUpdatedAt',
                headerName: 'Last Updated',
                flex: 1.2,
                renderCell: (params) => (params.value ? formatDateTime(params.value) : 'N/A'),
              },
            ]}
            getRowId={(row) => row.bookingId}
            components={{ Toolbar: CustomToolbar }}
            paginationMode="server"
            page={scanPage - 1}
            pageSize={scanPageSize}
            rowCount={scanTotalCount}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(p) => setScanPage(p + 1)}
            onPageSizeChange={(s) => { setScanPageSize(s); setScanPage(1); }}
            sortingOrder={['asc', 'desc']}
          />
        </Box>
      )}

      {tabValue === 3 && (
        <Card sx={{ backgroundColor: colors.primary[400] }}>
          <CardContent>
            <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb="20px">
              Full History & Records
            </Typography>
            <Box>
              {passengers.map((passenger, index) => (
                <Box key={passenger.id} mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" color={colors.grey[100]}>
                      {passenger.name} - Seat {passenger.seatNumber}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={passenger.status.toUpperCase()}
                        color={getStatusColor(passenger.status)}
                        size="small"
                      />
                      <Chip
                        label={passenger.paymentStatus.toUpperCase()}
                        color={getPaymentColor(passenger.paymentStatus)}
                        size="small"
                      />
                      <Chip
                        label={passenger.paymentFlowStatus.toUpperCase()}
                        color={getPaymentFlowColor(passenger.paymentFlowStatus)}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color={colors.grey[300]} mb={1}>
                    Booked on: {formatDate(passenger.bookingDate)} | Nationality: {passenger.nationality} | Phone:{' '}
                    {passenger.phone}
                  </Typography>
                  <Typography variant="body2" color={colors.blueAccent[300]} mb={1}>
                    QR Code: {passenger.hasQRCode ? 'Generated' : 'Not Generated'} | Scanned:{' '}
                    {passenger.scanStatus === 1 ? 'Yes' : 'No'} |
                    {passenger.qrGeneratedAt && ` QR Generated: ${formatDateTime(passenger.qrGeneratedAt)}`}
                  </Typography>
                  {passenger.specialRequests !== 'None' && (
                    <Typography variant="body2" color={colors.blueAccent[300]}>
                      Special Requests: {passenger.specialRequests}
                    </Typography>
                  )}
                  {index < passengers.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Alert */}
      {alert.show && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert({ show: false, message: '', type: 'success' })}
          sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
        >
          {alert.message}
        </Alert>
      )}
    </Box>
  );
};

export default ReportDetails;
